import { IRole, TBlkCxpRole } from '../../../types/settingsDataModule';
import { TPermissionCategory, TPermissionKey } from '../../../../utils/rolesDataModule';
import { permissionsConfig } from '../../../../utils/roles.config';
import { getParsedCountryNumber } from '../../../../utils/utils';

/**
 * Role names, plain-language summaries and the settings tab rules, kept in
 * one place so the settings pages, the users table, the role picker and the
 * account menu all describe a role the same way.
 */

/** Roles that can manage the whole organization. Used for self-lockout checks. */
export const ADMIN_ROLES: readonly string[] = ['SUPER_ADMIN', 'ROLE_BLKKCXP_ADMINISTRATOR'];

/** Roles that see the Organization tab. */
export const ORGANIZATION_TAB_ROLES: readonly TBlkCxpRole[] = [
  'SUPER_ADMIN',
  'ROLE_BLKKCXP_ADMINISTRATOR',
];

/** Roles that see the Users and Test numbers tabs, and can add test numbers. */
export const TEAM_TAB_ROLES: readonly TBlkCxpRole[] = [
  'SUPER_ADMIN',
  'ROLE_BLKKCXP_INTEGRATIONS_MANAGER',
  'ROLE_BLKKCXP_ACCOUNT_MANAGER',
  'ROLE_BLKKCXP_ADMINISTRATOR',
  'ROLE_BLKKCXP_CONTACTS_MANAGER',
];

interface RoleInfo {
  label: string;
  summary: string;
  /** Lower ranks are shown first. */
  rank: number;
}

const ROLE_INFO: Record<string, RoleInfo> = {
  SUPER_ADMIN: {
    label: 'Super admin',
    summary: 'Full access to every feature and setting.',
    rank: 0,
  },
  ROLE_BLKKCXP_ADMINISTRATOR: {
    label: 'Administrator',
    summary: 'Full access, including people, organization settings and billing.',
    rank: 1,
  },
  ROLE_BLKKCXP_ACCOUNT_MANAGER: {
    label: 'Account manager',
    summary: 'Manages people, organization details and sender IDs, and sends messages.',
    rank: 2,
  },
  ROLE_BLKKCXP_CONTENT_MANAGER: {
    label: 'Content manager',
    summary: 'Writes, schedules and sends messages and campaigns, and manages templates.',
    rank: 3,
  },
  ROLE_BLKKCXP_CONTACTS_MANAGER: {
    label: 'Contacts manager',
    summary: 'Adds, imports and organizes contacts, and writes messages to them.',
    rank: 4,
  },
  ROLE_BLKKCXP_INTEGRATIONS_MANAGER: {
    label: 'Integrations manager',
    summary: 'Manages API keys, webhooks and connected tools.',
    rank: 5,
  },
  ROLE_BLKKCXP_FINANCE_DATA_MANAGER: {
    label: 'Finance and data manager',
    summary: 'Buys credits, sees invoices and spend, and exports reports.',
    rank: 6,
  },
};

/** Keycloak housekeeping roles that mean nothing to an operator. */
function isHousekeeping(role: string): boolean {
  return /^default-roles|^offline_access$|^uma_authorization$/i.test(role);
}

/** "ROLE_BLKKCXP_CONTENT_MANAGER" → "Content manager", for roles not in the table. */
export function formatRoleName(role: string): string {
  const known = ROLE_INFO[role];
  if (known) return known.label;
  const words = (role || '')
    .replace(/BLKKCXP/gi, '')
    .replace(/\broles?\b/gi, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  return words ? words[0].toUpperCase() + words.slice(1) : role;
}

/**
 * One line on what a role allows. The IAM description wins when it reads as
 * a sentence; Keycloak placeholders such as "${role_admin}" fall back to ours.
 */
export function describeRole(role: string, iamRoles: readonly IRole[] = []): string {
  const fromIam = (iamRoles.find((r) => r.name === role)?.description || '').trim();
  if (fromIam && !fromIam.startsWith('$') && /\s/.test(fromIam)) return fromIam;
  return ROLE_INFO[role]?.summary ?? '';
}

/** Roles worth showing, most senior first, without Keycloak housekeeping roles. */
export function visibleRoles(roles: readonly string[] | null | undefined): string[] {
  return [...new Set((roles || []).filter((r) => !!r && !isHousekeeping(r)))].sort(
    (a, b) => (ROLE_INFO[a]?.rank ?? 50) - (ROLE_INFO[b]?.rank ?? 50) || a.localeCompare(b)
  );
}

/** "Administrator, Content manager". */
export function roleList(roles: readonly string[] | null | undefined): string {
  return visibleRoles(roles).map(formatRoleName).join(', ');
}

export function isAdmin(roles: readonly string[] | null | undefined): boolean {
  return (roles || []).some((r) => ADMIN_ROLES.includes(r));
}

/** Roles that let someone edit people. Losing all of them locks you out of this page. */
export const PEOPLE_MANAGER_ROLES: readonly string[] = [
  ...((permissionsConfig.USER_MANAGEMENT as unknown as Record<string, readonly string[]>)['edit-users'] || []),
];

/**
 * Roles that cannot be taken away from someone in the edit form, with why.
 * You keep the roles that let you manage people; the only enabled
 * administrator keeps the administrator role.
 */
export function lockedRolesFor(
  userRoles: readonly string[] | null | undefined,
  opts: { isSelf: boolean; isLastAdmin: boolean }
): Record<string, string> {
  const locked: Record<string, string> = {};
  for (const role of userRoles || []) {
    if (opts.isLastAdmin && ADMIN_ROLES.includes(role)) {
      locked[role] = 'The only administrator keeps this role. Make someone else an administrator first.';
    } else if (opts.isSelf && PEOPLE_MANAGER_ROLES.includes(role)) {
      locked[role] = 'You cannot remove this role from yourself. Ask another administrator.';
    }
  }
  return locked;
}

export function hasAnyRole(
  roles: readonly string[] | null | undefined,
  allowed: readonly string[]
): boolean {
  return (roles || []).some((r) => allowed.includes(r));
}

/** The picker hides housekeeping roles and lists the rest most senior first. */
export function pickableRoles(roles: readonly IRole[] | null | undefined): IRole[] {
  return (roles || [])
    .filter((r) => !!r?.name && !isHousekeeping(r.name))
    .sort(
      (a, b) =>
        (ROLE_INFO[a.name]?.rank ?? 50) - (ROLE_INFO[b.name]?.rank ?? 50) ||
        a.name.localeCompare(b.name)
    );
}

// ── Sign-in state of a team member ──

export type UserState = 'active' | 'pending' | 'disabled';

interface UserLike {
  enabled?: boolean;
  requiredActions?: string[] | null;
}

/**
 * Disabled people cannot sign in. Enabled people with a Keycloak required
 * action (set a password, verify the email) have not finished signing up.
 */
export function userState(user: UserLike | null | undefined): UserState {
  if (!user?.enabled) return 'disabled';
  return (user.requiredActions || []).length > 0 ? 'pending' : 'active';
}

/** Label and the word app-status reads its colour from. */
export const USER_STATE_STATUS: Record<UserState, { text: string; status: string }> = {
  active: { text: 'Active', status: 'success' },
  pending: { text: 'Invite pending', status: 'pending' },
  disabled: { text: 'Disabled', status: 'disabled' },
};

// ── "What you can do" on the Profile page ──

export interface Capability {
  category: TPermissionCategory;
  permission: TPermissionKey;
  label: string;
}

export interface CapabilityGroup {
  title: string;
  items: Capability[];
}

/** The permissions an operator asks about, in words they would use. */
export const CAPABILITY_GROUPS: readonly CapabilityGroup[] = [
  {
    title: 'Messages',
    items: [
      { category: 'MESSAGE_CONTENT_MANAGEMENT', permission: 'compose-sms', label: 'Write messages' },
      { category: 'MESSAGE_CONTENT_MANAGEMENT', permission: 'send-bulk-sms', label: 'Send to large audiences' },
      { category: 'MESSAGE_CONTENT_MANAGEMENT', permission: 'schedule-messages', label: 'Schedule and cancel sends' },
      { category: 'MESSAGE_CONTENT_MANAGEMENT', permission: 'send-test-msg', label: 'Send test messages' },
      { category: 'MESSAGE_CONTENT_MANAGEMENT', permission: 'create-message-templates', label: 'Create templates' },
    ],
  },
  {
    title: 'Contacts and campaigns',
    items: [
      { category: 'CONTACT_MANAGEMENT', permission: 'create-contacts', label: 'Add and edit contacts' },
      { category: 'CONTACT_MANAGEMENT', permission: 'import-contacts-csv-excel', label: 'Import contacts from a file' },
      { category: 'CONTACT_MANAGEMENT', permission: 'export-contacts', label: 'Export contacts' },
      { category: 'CAMPAIGN_MANAGEMENT', permission: 'launch-campaigns', label: 'Launch campaigns' },
    ],
  },
  {
    title: 'Reports and billing',
    items: [
      { category: 'REPORTS_AND_ANALYTICS', permission: 'view-delivery-reports', label: 'See delivery reports' },
      { category: 'REPORTS_AND_ANALYTICS', permission: 'export-reports-pdf-excel', label: 'Export reports' },
      { category: 'FINANCE_AND_BILLING', permission: 'view-balance-credits', label: 'See balance and credits' },
      { category: 'FINANCE_AND_BILLING', permission: 'purchase-credits', label: 'Buy credits' },
    ],
  },
  {
    title: 'People and settings',
    items: [
      { category: 'USER_MANAGEMENT', permission: 'create-users', label: 'Add people' },
      { category: 'USER_MANAGEMENT', permission: 'enable-disable-users', label: 'Disable and enable people' },
      { category: 'ACCOUNT_MANAGEMENT', permission: 'edit-account-settings', label: 'Edit organization details' },
      { category: 'ACCOUNT_MANAGEMENT', permission: 'manage-sender-ids', label: 'Manage sender IDs' },
      { category: 'API_AND_INTEGRATIONS', permission: 'generate-api-keys', label: 'Create API keys' },
    ],
  },
];

// ── Phone numbers ──

/** "+27821234567" → "+27 82 123 4567"; anything unparseable is shown as stored. */
export function displayPhone(raw: string | null | undefined): string {
  const value = (raw || '').toString().trim();
  if (!value) return '';
  const parsed = getParsedCountryNumber(value.startsWith('+') ? value : `+${value}`);
  return parsed?.isValid() ? parsed.formatInternational() : value;
}
